import { Box, SimpleGrid } from "@chakra-ui/react";
import { FillImage } from "components/images";
import { Section, sectionSpacingY } from "components/section";
import React from "react";
import { Card, HorizontalCard, type CardProps } from "ui/cards";
import { TextTitle } from "ui/text";
import firstCardImage from "../../../../public/assets/platform/reverse-etl/fill-card-1.png";
import secondCardImage from "../../../../public/assets/platform/reverse-etl/fill-card-2.png";
import thirdCardImage from "../../../../public/assets/platform/reverse-etl/fill-card-3.png";
import mobileThirdCardImage from "../../../../public/assets/platform/reverse-etl/fill-card-3-mobile.png";

const thirdCardImageAlt = "Illustration showing custom code functions that generate an order delivery date.";

const fillCardProps: Pick<CardProps, "variant" | "maxW" | "textMaxW" | "mediaRatio"> = {
  variant: "fill",
  maxW: { base: "cardMaxW", md: "100%" },
  textMaxW: "480px",
  mediaRatio: 647 / 535,
};

export function DeveloperFriendly() {
  return (
    <Section my={0} py={sectionSpacingY}>
      <TextTitle
        align="center"
        size="lg"
        maxW="820px"
        descriptionMaxW="640px"
        title="Extend the Hightouch platform with developer-friendly tooling"
        description="Hightouch is built with extensibility in mind. Data teams can use the REST API for complete, headless control — transforming Hightouch into the backbone for internal tools and embedded apps."
      />

      <Box mt={{ base: "biggest", md: "huge" }}>
        <SimpleGrid justifyItems={{ base: "center", md: "stretch" }} columns={{ base: 1, md: 2 }} gap="small">
          <Card
            {...fillCardProps}
            title="Build your own custom integrations"
            description="Connect to any API with granular control over endpoints, payloads, rate limits, error handling, and more. No code required."
            media={
              <FillImage
                src={firstCardImage}
                alt="Illustration depicting UI where a user connects to an API. The logic performed displays when a row is changed, run code to connect to an API with specific rate limits and error handling."
              />
            }
          />

          <Card
            {...fillCardProps}
            title="Sync to internal tools and databases"
            description="Use Hightouch to update transactional databases or publish messages into queues and streaming platforms."
            media={
              <FillImage
                src={secondCardImage}
                alt="Abstract illustration showing Hightouch acting as a means to connect a data warehouse to transactional databases and message distribution tools."
              />
            }
          />
        </SimpleGrid>

        <HorizontalCard
          mt="small"
          mx="auto"
          title="Write your own functions to enrich, transform, and sync data"
          description="Add custom logic to your pipelines by executing functions using AWS Lambda, Google Cloud Functions, or Azure Functions — all hosted in your own infrastructure."
          image={{
            src: thirdCardImage,
            alt: thirdCardImageAlt,
            ratio: 660 / 363,
          }}
          mobileImage={{
            src: mobileThirdCardImage,
            alt: thirdCardImageAlt,
            ratio: 348 / 258,
          }}
        />
      </Box>
    </Section>
  );
}
